import { useState } from 'react';
import { Input, Label, Message, Slider } from '@iwoca/orion';

import { Trans, t } from '@lingui/macro';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';

import InputFormatter from 'utils/inputFormatters/inputFormatters';
import { MIN_REPAYMENT_AMOUNT } from '../filterConfigs.helper';
import { getConfig } from '../LoanAmountFilter/LoanAmountFilter';
import standAloneCalculatorStyles from '../StandAloneCalculator.module.css';
import styles from './OverpaymentAmountFilter.module.css';

type TProps = {
  value?: string;
  onChange: (amount: string) => void;
};

export const OverpaymentAmountFilter = ({ value, onChange }: TProps) => {
  const [error, setError] = useState<string | null>(null);
  const { formatCurrency } = useNumberFormat();
  const { locale } = useLocale();
  const loanConfig = getConfig(locale);

  const handleBlur = (newValue: string) => {
    setError(null);
    if (Number(newValue) > loanConfig.max) {
      onChange(loanConfig.max.toString());
    } else if (Number(newValue) < Number(MIN_REPAYMENT_AMOUNT)) {
      onChange(MIN_REPAYMENT_AMOUNT.toString());
    }
  };

  const handleNumberChange = (value: string) => {
    setError(null);
    if (Number(value) < Number(MIN_REPAYMENT_AMOUNT)) {
      setError(t`Please enter a minimum of ${MIN_REPAYMENT_AMOUNT}`);
    }
    onChange(InputFormatter.Integer(value));
  };

  return (
    <div className={styles.container}>
      <div className={styles.inputContainer}>
        <Label htmlFor="monthly-repayment-amount" className={styles.label}>
          <Trans>Monthly repayment amount</Trans>
        </Label>
        <Input
          id="monthly-repayment-amount"
          className={styles.input}
          value={value}
          onChange={(e) => handleNumberChange(e.target.value)}
          onBlur={(e) => handleBlur(e.target.value)}
        />
      </div>
      <Slider
        labelClassName={standAloneCalculatorStyles.label}
        data-testid="monthly-repayment-amount-slider"
        value={value}
        step={10}
        leftLabel={formatCurrency(MIN_REPAYMENT_AMOUNT)}
        rightLabel={formatCurrency(loanConfig.max)}
        min={Number(MIN_REPAYMENT_AMOUNT)}
        max={loanConfig.max}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && (
        <Message
          className={styles.error}
          size="small"
          type="error"
          message={error}
        />
      )}
    </div>
  );
};
